import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { HOSPITAL, whatsappLink } from "@/lib/i18n";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    axios
      .get(`${API}/hospital-info`)
      .then((r) => setInfo(r.data))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form_success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form_error);
    } finally {
      setLoading(false);
    }
  };

  const address = (lang === "id"
    ? info?.address_id
    : info?.address_en) || (lang === "id" ? HOSPITAL.address_id : HOSPITAL.address_en);
  const phoneDisplay = info?.phone || HOSPITAL.phone_display;
  const emailAddr = info?.email || HOSPITAL.email;
  const hoursText = (lang === "id"
    ? info?.hours_id
    : info?.hours_en) ||
    (lang === "id"
      ? "IGD 24 Jam | Poliklinik: Senin - Sabtu, 08.00 - 20.00"
      : "ER 24 Hours | Clinic: Mon - Sat, 08.00 - 20.00");
  const whatsappRaw = info?.whatsapp;
  const whatsappHref = whatsappRaw ? `https://wa.me/${whatsappRaw}` : whatsappLink();

  // Google Maps embed prefers lat/lng when available; falls back to text query.
  const mapSrc =
    info && Number.isFinite(info.latitude) && Number.isFinite(info.longitude)
      ? `https://www.google.com/maps?q=${info.latitude},${info.longitude}&z=17&output=embed`
      : `https://www.google.com/maps?q=${encodeURIComponent(
          info?.maps_query ||
            "RS Bhayangkara Tarakan Jalan Karya Bersama Juata Laut Tarakan Utara"
        )}&output=embed`;

  const items = [
    {
      icon: MapPin,
      label: t.contact.address_label,
      value: address,
      testid: "contact-address",
    },
    {
      icon: Phone,
      label: t.contact.phone_label,
      value: phoneDisplay,
      href: whatsappHref,
      testid: "contact-phone",
    },
    {
      icon: Mail,
      label: t.contact.email_label,
      value: emailAddr,
      href: `mailto:${emailAddr}`,
      testid: "contact-email",
    },
    {
      icon: Clock,
      label: t.contact.hours_label,
      value: hoursText,
      testid: "contact-hours",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-y bg-slate-50"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4">{t.contact.overline}</div>
          <h2
            data-testid="contact-title"
            className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
          >
            {t.contact.title}
          </h2>
          <p className="text-slate-600 leading-relaxed">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left: info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 space-y-4"
          >
            {items.map((it) => {
              const Content = (
                <div
                  data-testid={it.testid}
                  className="flex gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <div className="shrink-0 h-11 w-11 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center">
                    <it.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                      {it.label}
                    </div>
                    <div className="text-sm text-slate-700 leading-relaxed">
                      {it.value}
                    </div>
                  </div>
                </div>
              );
              return it.href ? (
                <a
                  key={it.testid}
                  href={it.href}
                  target={it.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="block"
                >
                  {Content}
                </a>
              ) : (
                <div key={it.testid}>{Content}</div>
              );
            })}

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
              <iframe
                title="RS Bhayangkara Tarakan Map"
                data-testid="contact-map"
                src={mapSrc}
                className="w-full h-64 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white rounded-3xl border border-slate-100 p-6 sm:p-8 shadow-sm"
          >
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-1">
              {t.contact.form_title}
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              {t.contact.form_subtitle}
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  {t.contact.form_name}
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-form-name"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white transition"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  {t.contact.form_email}
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-form-email"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white transition"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  {t.contact.form_phone}
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-form-phone"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white transition"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  {t.contact.form_subject}
                </label>
                <input
                  required
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  data-testid="contact-form-subject"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white transition"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                {t.contact.form_message}
              </label>
              <textarea
                required
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                data-testid="contact-form-message"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white resize-none transition"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="mt-6 inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white font-medium px-6 py-3 rounded-full transition shadow-md shadow-blue-900/20"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {t.contact.form_submit}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
